import { rem } from 'polished'
import { useCallback, useContext, useId, useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import HelpPane from 'components/Common/HelpPane'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isLEStaff } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import {
  formatHeadlineCancellationPolicyStartForDisplay,
  selectDateFormattingLocale,
} from 'lib/datetime/dateUtils'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: none;
`

interface Props {
  rates: Array<App.EnrichedPropertyRate>
  selectedRateId: string | undefined
  onChange: (rateId: string) => void
  propertyTimezone: string
}

/**
 * Get cancellation policy display text
 */
interface CancellationDisplayText {
  primary: string
  secondary?: string
  highlightPrimary: boolean
}

interface RefundabilityDisplayedModeOptions {
  price: number
  memberPrice: number
  canViewLuxPlusBenefits: boolean
  luxPlusEnabled: boolean
  luxPlusTourEnabled: boolean
  isGhaPricing: boolean
}

function getRefundabilityDisplayedMode({
  price,
  memberPrice,
  canViewLuxPlusBenefits,
  luxPlusEnabled,
  luxPlusTourEnabled,
  isGhaPricing,
}: RefundabilityDisplayedModeOptions): App.LuxPlusPriceStackMode {
  if (!luxPlusEnabled) {
    return 'regular-only'
  } else if (isGhaPricing && memberPrice > 0 && memberPrice < price) {
    return 'regular-prominent'
  } else if (!luxPlusTourEnabled) {
    return 'regular-only'
  } else if (!canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
    return 'regular-prominent'
  } else if (canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
    return 'member-prominent'
  } else {
    return 'regular-only'
  }
}

export function getDisplayedRatePrice(
  rate: App.EnrichedPropertyRate,
  displayMode: App.LuxPlusPriceStackMode,
  preferRegularPrice: boolean = false,
): number {
  return getRatePriceByDisplayMode(
    rate,
    displayMode,
    'displayPrice',
    preferRegularPrice,
  )
}

export function getSellingRatePrice(
  rate: App.EnrichedPropertyRate,
): number {
  return rate.luxPlusTotals?.sellingPrice ?? rate.totals.sellingPrice
}

function getRatePriceByDisplayMode(
  rate: App.EnrichedPropertyRate,
  displayMode: App.LuxPlusPriceStackMode,
  priceKey: 'displayPrice' | 'sellingPrice',
  preferRegularPrice: boolean = false,
): number {
  const memberPrice = rate.luxPlusTotals?.[priceKey] ?? 0
  const regularPrice = rate.totals[priceKey]

  if (preferRegularPrice) {
    return regularPrice
  }

  switch (displayMode) {
    case 'member-only':
    case 'member-prominent':
    case 'member-compact':
    case 'member-checkout-total':
      return memberPrice || regularPrice
    case 'regular-only':
    case 'regular-prominent':
    case 'regular-with-luxPlus':
    default:
      return regularPrice
  }
}

function getCancellationText(
  intl: ReturnType<typeof useIntl>,
  rate: App.EnrichedPropertyRate,
  timezone: string,
  dateFormattingLocale: string,
): CancellationDisplayText {
  const { headlineCancellationPolicyType, headlineCancellationPolicyStart } = rate

  const nonRefundableLabel = intl.formatMessage({
    id: 'xvWHpDA1-Kplt1HmWGii',
    defaultMessage: 'Non refundable',
    description: 'Cancellation policy label for non-refundable property rates',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'NmC8LTH4QQoMwIso1Xbh',
    defaultMessage: 'Free cancellation',
    description: 'Cancellation policy label for fully refundable property rates',
  })
  const partiallyRefundableLabel = intl.formatMessage({
    id: 'ZznnSheC7alEPdRGvXTi',
    defaultMessage: 'Partially refundable',
    description: 'Cancellation policy label for partially refundable property rates',
  })

  if (headlineCancellationPolicyType === 'nonRefundable') {
    return {
      primary: nonRefundableLabel,
      highlightPrimary: false,
    }
  }

  const refundText = headlineCancellationPolicyType === 'fullyRefundable'
    ? freeCancellationLabel
    : partiallyRefundableLabel

  // Format date: same calendar resolution as `@luxuryescapes/lib-refunds` `formattedShortDateByTimezone`,
  // then locale-aware weekday/month via Intl (`formatHeadlineCancellationPolicyStartForDisplay`).
  if (headlineCancellationPolicyStart) {
    const formattedDate = formatHeadlineCancellationPolicyStartForDisplay(
      headlineCancellationPolicyStart,
      timezone,
      dateFormattingLocale,
    )

    return {
      primary: refundText,
      secondary: intl.formatMessage(
        {
          id: 'hv1uXBUiZ3e8KSTtpNec',
          defaultMessage: 'until {date}',
          description: 'Date suffix for cancellation policy in the date selection or calendar picker',
        },
        { date: formattedDate },
      ),
      highlightPrimary: true,
    }
  }

  return {
    primary: refundText,
    highlightPrimary: true,
  }
}

function PropertyRefundabilitySelector(props: Props) {
  const {
    rates,
    selectedRateId,
    onChange,
    propertyTimezone,
  } = props

  const intl = useIntl()
  const radioGroupName = useId()
  const showModal = useContext(ModalContext)
  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const dateFormattingLocale = selectDateFormattingLocale(geoLocale, currentRegionCode)

  const canShowStaffLabel = useAppSelector(state => isLEStaff(state) || state.auth.account.isSpoofed)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusTourEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)

  const selectedRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const selectedRateDisplayedMode = useMemo(() => {
    if (!selectedRate) {
      return 'regular-only'
    }

    return getRefundabilityDisplayedMode({
      price: selectedRate.totals.displayPrice,
      memberPrice: selectedRate.luxPlusTotals?.displayPrice ?? 0,
      canViewLuxPlusBenefits,
      luxPlusEnabled,
      luxPlusTourEnabled,
      isGhaPricing,
    })
  }, [selectedRate, canViewLuxPlusBenefits, luxPlusEnabled, luxPlusTourEnabled, isGhaPricing])

  const selectedRatePrice = useMemo(() => {
    if (!selectedRate) {
      return 0
    }

    return isAgentHubEnabled
      ? getDisplayedRatePrice(selectedRate, selectedRateDisplayedMode, true)
      : getSellingRatePrice(selectedRate)
  }, [selectedRate, selectedRateDisplayedMode])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rateId = event.target.value
    onChange(rateId)
  }, [onChange])

  const handleShowCancellationDetails = useCallback((rate: App.EnrichedPropertyRate | undefined, label: string) => {
    if (!rate) {
      return
    }

    const { description: policyDescriptions } = buildAccommodationCancellationPolicies({
      fullRefundableUntil: rate.fullyRefundableUntil,
      cancellationPolicies: rate.cancellationPolicies.map(({
        startIsoDateTime,
        endIsoDateTime,
        penaltyPrice,
        isFullyNonRefundable,
        originalDescription,
      }) => ({
        amount: penaltyPrice,
        percent: 0,
        description: originalDescription,
        start: startIsoDateTime,
        end: endIsoDateTime,
        nonRefundable: isFullyNonRefundable,
      })),
    }, {
      timezone: propertyTimezone,
      currencyCode: rate.currency,
      hideExcludesFlights: !rate.includesFlights,
      locale: intl.locale,
    })

    showModal(<Modal title={label}>
      {policyDescriptions.map((line, index) => <BodyText variant="large" as="p" key={index}>{line}</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [propertyTimezone, showModal, intl.locale])

  if (rates.length === 0) {
    return null
  }

  return (
    <RadioGroup>
      {rates.map((rate) => {
        const text = getCancellationText(intl, rate, propertyTimezone, dateFormattingLocale)
        const isSelected = rate.rateId === selectedRateId
        let staffRateLabel: string | undefined = undefined
        if (canShowStaffLabel && rate.staffLabel) {
          staffRateLabel = intl.formatMessage({
            id: 'HJnIuZ3JW8_X7JmL0chU',
            defaultMessage: '{staffLabel} (label only shows for staff)',
            description: 'Staff-only label providing additional rate details',
          }, { staffLabel: rate.staffLabel })
        }
        // Match the row delta to the same displayed total price used by the selected package card.
        const rateDisplayedMode = getRefundabilityDisplayedMode({
          price: rate.totals.displayPrice,
          memberPrice: rate.luxPlusTotals?.displayPrice ?? 0,
          canViewLuxPlusBenefits,
          luxPlusEnabled,
          luxPlusTourEnabled,
          isGhaPricing,
        })
        const ratePrice = isAgentHubEnabled
          ? getDisplayedRatePrice(rate, rateDisplayedMode, true)
          : getSellingRatePrice(rate)
        const priceDifference = selectedRate && rate.rateId !== selectedRate.rateId && selectedRatePrice
          ? ratePrice - selectedRatePrice
          : 0

        return (
          <StyledRadioInput
            key={rate.rateId}
            name={radioGroupName}
            value={rate.rateId}
            onChange={handleChange}
            checked={isSelected}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
              <BodyText
                variant="medium"
                weight={text.highlightPrimary ? 'bold' : undefined}
                colour={text.highlightPrimary ? 'highlight-primary-dark' : undefined}
                wrap="no-wrap"
              >
                {text.primary}
              </BodyText>
              {text.secondary && (
                <BodyText variant="medium" wrap="no-wrap">
                  {text.secondary}
                </BodyText>
              )}
              {staffRateLabel && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {staffRateLabel}
                </BodyText>
              )}
              {!isSelected && priceDifference > 0 && (
                <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
                  <FormattedMessage
                    id="mGHDJoYlHarKV4E9f2Ln"
                    defaultMessage="+{price}"
                    description="Positive price difference with plus sign for refundability option"
                    values={{ price: <FormatCurrency value={priceDifference} format="roundedDollar" /> }}
                  />
                </BodyText>
              )}
              {!isSelected && priceDifference < 0 && (
                <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
                  <FormatCurrency value={priceDifference} format="roundedDollar" />
                </BodyText>
              )}
              <CancellationClickable
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  handleShowCancellationDetails(rate, text.primary)
                }}
              >
                <InlineIcon Icon={LineInfoCircleIcon} />
              </CancellationClickable>
            </Group>
          </StyledRadioInput>
        )
      })}
    </RadioGroup>
  )
}

export default PropertyRefundabilitySelector
